import React, { useState, useEffect } from 'react';
import { apiService, FileCategory } from '../services/api';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  FileCheck, 
  ClipboardList, 
  Shield, 
  FileText, 
  Bell,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

interface CategoryFormData {
  strName: string;
  intSortOrder: number;
  bolIsActive: boolean;
}

const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<FileCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FileCategory | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>({
    strName: '',
    intSortOrder: 1,
    bolIsActive: true
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await apiService.getAllCategories();
        setCategories(data);
      } catch (error) {
        // Handle error silently
      }
    };

    fetchCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAllCategories(true); // Include inactive categories for management
      // Check for categories with undefined IDs
      const invalidCategories = data.filter(cat => !cat.strGUID);
      if (invalidCategories.length > 0) {
        // Found categories with undefined IDs
      }
      setCategories(data);
    } catch (error) {
      // Error loading categories
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      strName: '',
      intSortOrder: 1,
      bolIsActive: true
    });
    setEditingCategory(null);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.strName.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      if (editingCategory) {
        await apiService.updateCategory(editingCategory.strGUID, {
          strName: formData.strName,
          bolIsActive: formData.bolIsActive,
          intSortOrder: formData.intSortOrder
        });
        setSuccess('Category updated successfully');
      } else {
        await apiService.createCategory({
          strName: formData.strName,
          intSortOrder: formData.intSortOrder
        });
        setSuccess('Category created successfully');
      }
      
      setFormData({ strName: '', intSortOrder: 1, bolIsActive: true });
      setEditingCategory(null);
      loadCategories();
    } catch (error) {
      // Error saving category
      setError(error instanceof Error ? error.message : 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category: FileCategory) => {
    setEditingCategory(category);
    setFormData({
      strName: category.strName,
      intSortOrder: category.intSortOrder,
      bolIsActive: category.bolIsActive
    });
    setShowForm(true);
  };

  const handleDelete = async (categoryId: string) => {
    if (!categoryId) {
      setError('Invalid category ID');
      return;
    }
    
    // Find the category to check if it has files
    const category = categories.find(cat => cat.strGUID === categoryId);
    if (category && category.downloadCount && category.downloadCount > 0) {
      setError(`Cannot delete category '${category.strName}' because it contains ${category.downloadCount} file(s). Please remove all files from this category before deleting it.`);
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      await apiService.deleteCategory(categoryId);
      setSuccess('Category deleted successfully');
      loadCategories();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      // Error deleting category
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete category';
      setError(errorMessage);
      
      // Clear error message after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (category: FileCategory) => {
    try {
      setLoading(true);
      setError(null);
      const newStatus = !category.bolIsActive;
      
      console.log('Updating category:', {
        id: category.strGUID,
        name: category.strName,
        newStatus: newStatus,
        sortOrder: category.intSortOrder
      });
      
      await apiService.updateCategory(category.strGUID, {
        strName: category.strName,
        bolIsActive: newStatus,
        intSortOrder: category.intSortOrder
      });
      
      setSuccess(`Category ${newStatus ? 'activated' : 'deactivated'} successfully`);
      loadCategories();
    } catch (error) {
      console.error('Error updating category status:', error);
      // Error toggling category status
      setError(`Failed to update category status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const getIconComponent = () => {
    return <FileCheck className="h-5 w-5" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <FileCheck className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Category Management</h1>
              <p className="text-gray-600 mt-1">Create and manage file categories for better organization</p>
              <p className="text-sm text-gray-500 mt-2">
                💡 Categories with files cannot be deleted. Remove all files first, then delete the category.
              </p>
            </div>
          </div>
          <Button 
            onClick={() => setShowForm(true)} 
            disabled={loading}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200 px-6 py-3"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Category
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg p-4 mb-6 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
              <AlertCircle className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-red-800">Error</h3>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-gradient-to-r from-green-50 to-green-100 border-l-4 border-green-500 rounded-lg p-4 mb-6 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-green-800">Success</h3>
              <p className="text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Category Form */}
      {showForm && (
        <Card className="border-2 border-blue-100 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Plus className="h-4 w-4 text-white" />
                </div>
                <CardTitle className="text-xl font-bold text-gray-900">
                  {editingCategory ? 'Edit Category' : 'Create New Category'}
                </CardTitle>
              </div>
              <Button variant="ghost" size="sm" onClick={resetForm} className="hover:bg-blue-200">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-semibold text-gray-700">
                    Category Name *
                  </Label>
                  <Input
                    id="name"
                    value={formData.strName}
                    onChange={(e) => setFormData({ ...formData, strName: e.target.value })}
                    placeholder="Enter category name"
                    className="border-2 focus:border-blue-500 focus:ring-blue-500 transition-all duration-200"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sortOrder" className="text-sm font-semibold text-gray-700">
                    Sort Order *
                  </Label>
                  <Input
                    id="sortOrder"
                    type="number"
                    value={formData.intSortOrder}
                    onChange={(e) => setFormData({ ...formData, intSortOrder: parseInt(e.target.value) || 1 })}
                    placeholder="Enter unique sort order"
                    className="border-2 focus:border-blue-500 focus:ring-blue-500 transition-all duration-200"
                    required
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gray-700">Status</Label>
                <div className="grid grid-cols-2 gap-4">
                  <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    formData.bolIsActive === true 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="isActive"
                      value="true"
                      checked={formData.bolIsActive === true}
                      onChange={(e) => setFormData({ ...formData, bolIsActive: e.target.value === 'true' })}
                      className="sr-only"
                    />
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.bolIsActive === true 
                          ? 'border-green-500 bg-green-500' 
                          : 'border-gray-300'
                      }`}>
                        {formData.bolIsActive === true && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Active</div>
                        <div className="text-sm text-gray-500">Category will be visible</div>
                      </div>
                    </div>
                  </label>
                  
                  <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    formData.bolIsActive === false 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="isActive"
                      value="false"
                      checked={formData.bolIsActive === false}
                      onChange={(e) => setFormData({ ...formData, bolIsActive: e.target.value === 'true' })}
                      className="sr-only"
                    />
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.bolIsActive === false 
                          ? 'border-red-500 bg-red-500' 
                          : 'border-gray-300'
                      }`}>
                        {formData.bolIsActive === false && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Inactive</div>
                        <div className="text-sm text-gray-500">Category will be hidden</div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={resetForm}
                  className="border-2 hover:border-gray-400 transition-all duration-200"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingCategory ? 'Update Category' : 'Create Category'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Categories List */}
      {loading && !showForm ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-white border-t-transparent"></div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Loading Categories</h3>
              <p className="text-gray-600">Please wait while we fetch your categories...</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <Card key={category.strGUID} className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-blue-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                      category.bolIsActive 
                        ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg' 
                        : 'bg-gradient-to-br from-gray-400 to-gray-500 text-white shadow-lg'
                    }`}>
                      {getIconComponent()}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {category.strName}
                      </CardTitle>
                      <CardDescription className="text-sm text-gray-500">
                        {category.downloadCount || 0} files
                        {(category.downloadCount ? category.downloadCount > 0 : false) && (
                          <span className="ml-2 text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
                            Cannot delete
                          </span>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                      Sort: {category.intSortOrder}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold transition-all duration-300 ${
                      category.bolIsActive 
                        ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300' 
                        : 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300'
                    }`}>
                      {category.bolIsActive ? '✓ Active' : '✗ Inactive'}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-100">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(category)}
                    className="text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant={category.bolIsActive ? "outline" : "default"}
                    size="sm"
                    onClick={() => handleToggleStatus(category)}
                    className={`transition-all duration-200 ${
                      category.bolIsActive 
                        ? 'border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400' 
                        : 'bg-red-500 hover:bg-red-600 text-white border-red-500'
                    }`}
                  >
                    {category.bolIsActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => category.strGUID && handleDelete(category.strGUID)}
                    className={`transition-all duration-200 ${
                      (category.downloadCount ? category.downloadCount > 0 : false)
                        ? 'text-gray-400 hover:text-gray-500 cursor-not-allowed'
                        : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                    }`}
                    disabled={!category.strGUID || (category.downloadCount ? category.downloadCount > 0 : false)}
                    title={
                      (category.downloadCount ? category.downloadCount > 0 : false)
                        ? `Cannot delete category with ${category.downloadCount} file(s). Remove all files first.`
                        : 'Delete category'
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && categories.length === 0 && !showForm && (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
              <FileCheck className="h-10 w-10 text-gray-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">No Categories Found</h3>
              <p className="text-gray-600 mt-2">Create your first category to start organizing your files</p>
            </div>
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200 mt-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First Category
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement; 